/**
 * PanoramaLoader - Loads cube map textures for panoramas
 */

import * as THREE from "three"

export class PanoramaLoader {
  constructor(baseUrl = "", tourId = null, panoramaConfigs = {}) {
    this.loader = new THREE.CubeTextureLoader()
    this.cache = new Map()
    this.baseUrl = baseUrl
    this.tourId = tourId
    this.panoramaConfigs = panoramaConfigs // Store panorama configs for custom paths
  }

  /**
   * Update panorama configurations (for custom paths)
   * @param {Object} configs - Panorama configurations
   */
  setPanoramaConfigs(configs) {
    this.panoramaConfigs = configs
  }

  /**
   * Load a panorama cube map
   * @param {string|number} panoId - Panorama ID (2-10)
   * @param {Object} customPaths - Optional custom panorama paths (overrides config)
   * @returns {Promise<THREE.CubeTexture>}
   */
  load(panoId, customPaths = null) {
    return new Promise((resolve, reject) => {
      // Check cache
      if (this.cache.has(panoId)) {
        console.log(`✅ Panorama ${panoId} loaded from cache`)
        resolve(this.cache.get(panoId))
        return
      }

      let paths

      // Priority 1: Use custom paths passed directly to load()
      if (customPaths && customPaths.right && customPaths.left) {
        paths = [
          customPaths.right,
          customPaths.left,
          customPaths.up,
          customPaths.down,
          customPaths.front,
          customPaths.back,
        ]
        console.log(`📦 Loading panorama ${panoId} with custom paths`)
      }
      // Priority 2: Use panoramaPaths from config (cloud storage URLs)
      else if (this.panoramaConfigs[panoId]?.panoramaPaths) {
        const pp = this.panoramaConfigs[panoId].panoramaPaths
        paths = [
          pp.right,
          pp.left,
          pp.up,
          pp.down,
          pp.front,
          pp.back,
        ]
        console.log(`📦 Loading panorama ${panoId} from cloud storage`)
      }
      // Priority 3: Construct paths based on whether we're using dynamic tours or legacy assets
      else if (this.tourId) {
        // Dynamic tour: baseUrl already includes full tour path
        const basePath = `${this.baseUrl}/panoramas/${panoId}`
        paths = [
          `${basePath}/${panoId}_r.jpg`, // right (positive X)
          `${basePath}/${panoId}_l.jpg`, // left (negative X)
          `${basePath}/${panoId}_u.jpg`, // up (positive Y)
          `${basePath}/${panoId}_d.jpg`, // down (negative Y)
          `${basePath}/${panoId}_f.jpg`, // front (positive Z)
          `${basePath}/${panoId}_b.jpg`, // back (negative Z)
        ]
        console.log(`📦 Loading panorama ${panoId} from dynamic tour`)
      } else {
        // Legacy: assets are in panoramas/ folder
        paths = [
          `${this.baseUrl}panoramas/${panoId}_r.jpg`, // right (positive X)
          `${this.baseUrl}panoramas/${panoId}_l.jpg`, // left (negative X)
          `${this.baseUrl}panoramas/${panoId}_u.jpg`, // up (positive Y)
          `${this.baseUrl}panoramas/${panoId}_d.jpg`, // down (negative Y)
          `${this.baseUrl}panoramas/${panoId}_f.jpg`, // front (positive Z)
          `${this.baseUrl}panoramas/${panoId}_b.jpg`, // back (negative Z)
        ]
        console.log(`📦 Loading panorama ${panoId} from legacy assets`)
      }

      console.log(`   First face path:`, paths[0])

      this.loader.load(
        paths,
        (texture) => {
          // Set correct color space for sRGB images (photos)
          texture.colorSpace = THREE.SRGBColorSpace

          // Improve texture filtering for better quality
          texture.minFilter = THREE.LinearMipmapLinearFilter
          texture.magFilter = THREE.LinearFilter
          texture.generateMipmaps = true

          console.log(
            `✅ Panorama ${panoId} loaded successfully (sRGB color space)`,
          )
          this.cache.set(panoId, texture)
          resolve(texture)
        },
        undefined,
        (error) => {
          console.error(`❌ Error loading panorama ${panoId}:`, error)
          reject(error)
        },
      )
    })
  }

  /**
   * Preload multiple panoramas
   * @param {Array<string|number>} panoIds - Array of panorama IDs
   * @returns {Promise<Array<THREE.CubeTexture>>}
   */
  async preload(panoIds) {
    console.log(`📦 Preloading ${panoIds.length} panoramas...`)
    const promises = panoIds.map((id) => this.load(id))
    return Promise.all(promises)
  }

  /**
   * Clear cache to free memory
   */
  clearCache() {
    this.cache.forEach((texture) => {
      texture.dispose()
    })
    this.cache.clear()
    console.log("🗑️ Panorama cache cleared")
  }

  /**
   * Get cached texture
   * @param {string|number} panoId
   * @returns {THREE.CubeTexture|null}
   */
  getCached(panoId) {
    return this.cache.get(panoId) || null
  }
}
